import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { fetchCameraRawData } from "../../services/cameraDetailApi.ts";

/** `device_type` value for Bullet camera — used by Add dialog and POST /cameras. */
export const DEVICE_TYPE_BULLET = "Bullet camera";

export interface AddCameraFormValues {
  cameraName: string;
  /** Full stream URL as sent to API `streaming_url` (e.g. rtsp://host:554/path). */
  streamUrl: string;
  isActive: boolean;
  ip: string;
  deviceType: string;
  apiBaseUrl: string;
  port: string;
  telnetUsername: string;
  telnetPassword: string;
  allowAuthOnly: boolean;
  streamingId: string;
  streamingPassword: string;
  /** RTSP path segment after host:port; editable only for Bullet camera. */
  streamingPath: string;
}

/** Empty name/URL for Add Camera; edit mode uses {@link mapApiRecordToAddCameraForm}. */
const DEFAULT_VALUES: AddCameraFormValues = {
  cameraName: "",
  streamUrl: "",
  isActive: true,
  ip: "",
  deviceType: "RX1000P",
  apiBaseUrl: "/osc/commands/execute",
  port: "554",
  telnetUsername: "root",
  telnetPassword: "",
  allowAuthOnly: true,
  streamingId: "",
  streamingPassword: "",
  /** Empty for non-Bullet so placeholder `/streaming` shows; Bullet edits this. */
  streamingPath: "",
};

function mapApiRecordToAddCameraForm(
  d: Record<string, unknown>,
): AddCameraFormValues {
  const knownTypes = new Set([
    "RX1000P",
    "RX0",
    "Generic RTSP",
    DEVICE_TYPE_BULLET,
  ]);
  const typeRaw = String(d.type ?? d.device_type ?? "RX1000P").trim() || "RX1000P";
  const deviceType = knownTypes.has(typeRaw) ? typeRaw : "Generic RTSP";

  const streamingUrl = String(d.streaming_url ?? d.stream_url ?? "").trim();
  let ip = String(d.ip_address ?? d.camera_ip ?? "").trim();
  let port =
    d.port != null && String(d.port).trim() !== ""
      ? String(d.port).trim()
      : "554";
  let streamingPath = "";

  if (streamingUrl.startsWith("rtsp://") || streamingUrl.startsWith("rtsps://")) {
    try {
      const normalized = streamingUrl
        .replace(/^rtsps:/i, "https:")
        .replace(/^rtsp:/i, "http:");
      const u = new URL(normalized);
      if (!ip) ip = u.hostname;
      if (u.port) port = u.port;
      const pathname = u.pathname.replace(/^\/+/, "");
      if (deviceType === DEVICE_TYPE_BULLET) {
        streamingPath = pathname ? `/${pathname}` : "/streaming";
      }
    } catch {
      /* keep ip/port from API fields */
    }
  }

  const allowAuthRaw = d.allow_auth ?? d.allow_auth_only;
  const allowAuthOnly =
    allowAuthRaw === true ||
    allowAuthRaw === 1 ||
    (typeof allowAuthRaw === "string" &&
      allowAuthRaw.trim().toLowerCase() === "true");

  const isBullet = deviceType === DEVICE_TYPE_BULLET;
  const useStreamAuth = !isBullet && allowAuthOnly;

  const rawApiBase = String(d.api_base_url ?? "/osc/commands/execute").trim();
  const apiBaseUrl = rawApiBase || "/osc/commands/execute";

  const streamUrlDisplay =
    streamingUrl ||
    (ip
      ? deviceType === DEVICE_TYPE_BULLET
        ? (() => {
            const raw = streamingPath || "/streaming";
            const pathSeg = raw.replace(/^\/+/, "");
            if (!pathSeg) return `rtsp://${ip}:${port}/`;
            return `rtsp://${ip}:${port}/${pathSeg}`;
          })()
        : `rtsp://${ip}:${port}/`
      : "");

  return {
    cameraName: String(d.name ?? "").trim(),
    streamUrl: streamUrlDisplay,
    isActive: Boolean(d.is_active),
    ip: ip || "",
    deviceType,
    apiBaseUrl,
    port: port || "554",
    telnetUsername: String(d.user ?? d.telnet_username ?? "root"),
    telnetPassword: String(d.password ?? d.telnet_password ?? ""),
    allowAuthOnly: isBullet ? false : allowAuthOnly,
    streamingId: useStreamAuth
      ? String(d.streaming_id ?? d.streaming_username ?? "")
      : "",
    streamingPassword: useStreamAuth
      ? String(d.streaming_password ?? "")
      : "",
    streamingPath: isBullet ? streamingPath || "/streaming" : "",
  };
}

export interface AddCameraDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (values: AddCameraFormValues) => void | Promise<void>;
  /** When set with `apiBase`, loads camera on open and Save calls `onEditApply`. */
  editingCameraId?: string | null;
  onEditApply?: (
    cameraId: string,
    values: AddCameraFormValues,
  ) => void | Promise<void>;
  /** Required when `editingCameraId` is set. */
  apiBase?: string;
}

/** RTSP/stream URL for POST /cameras from add-camera form values. */
export function buildAddCameraStreamUrl(values: AddCameraFormValues): string {
  const direct = values.streamUrl?.trim();
  if (direct) return direct;
  const ip = values.ip.trim();
  const port = (values.port || "554").trim();
  if (values.deviceType === DEVICE_TYPE_BULLET) {
    const raw = values.streamingPath?.trim() || "/streaming";
    const pathSeg = raw.replace(/^\/+/, "");
    if (!pathSeg) return `rtsp://${ip}:${port}/`;
    return `rtsp://${ip}:${port}/${pathSeg}`;
  }
  return `rtsp://${ip}:${port}/`;
}

/**
 * POST /cameras request body (Create camera).
 * Requires unique `name` and `streaming_url`; `is_active` included per API contract.
 */
export interface PostCamerasCreateRequestBody {
  name: string;
  streaming_url: string;
  is_active: boolean;
}

export function buildPostCamerasCreateBody(
  values: AddCameraFormValues,
): PostCamerasCreateRequestBody {
  const streaming_url = buildAddCameraStreamUrl(values);
  const name =
    values.cameraName?.trim() ||
    values.streamUrl?.trim() ||
    values.streamingId?.trim() ||
    `Camera ${values.ip.trim() || "unknown"}`;
  return {
    name,
    streaming_url,
    is_active: Boolean(false),
  };
}

/** PUT /cameras/{id} — same shape as create for this API. */
export type PutCamerasUpdateRequestBody = PostCamerasCreateRequestBody;

export function buildPutCamerasUpdateBody(
  values: AddCameraFormValues,
): PutCamerasUpdateRequestBody {
  return buildPostCamerasCreateBody(values);
}

/** Label + required asterisk, with info icon directly beside the label text. */
function RequiredLabelWithInfo({
  children,
  infoTitle,
  infoAriaLabel,
}: {
  children: React.ReactNode;
  infoTitle: string;
  infoAriaLabel: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        flexWrap: "wrap",
        gap: 0.25,
        mb: 0.5,
      }}
    >
      <Typography
        component="label"
        sx={{
          fontFamily: "Roboto, sans-serif",
          fontSize: 14,
          fontWeight: 500,
          color: "#212121",
        }}
      >
        {children}{" "}
        <Typography component="span" sx={{ color: "#D32F2F" }}>
          *
        </Typography>
      </Typography>
      <Tooltip title={infoTitle}>
        <IconButton
          size="small"
          aria-label={infoAriaLabel}
          sx={{
            color: "rgba(0,0,0,0.54)",
            p: 0.25,
            ml: 0.25,
            verticalAlign: "middle",
          }}
        >
          <InfoOutlined sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function AddCameraDialog({
  open,
  onClose,
  onApply,
  editingCameraId,
  onEditApply,
  apiBase,
}: AddCameraDialogProps) {
  const [values, setValues] = useState<AddCameraFormValues>(DEFAULT_VALUES);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setValues({ ...DEFAULT_VALUES });
      setLoadError(null);
      setLoadingEdit(false);
      return;
    }

    setLoadError(null);

    const editId = editingCameraId?.trim();
    const base = apiBase?.trim();

    if (!editId || !base) {
      setValues({ ...DEFAULT_VALUES });
      setLoadingEdit(false);
      return;
    }

    const ac = new AbortController();
    setLoadingEdit(true);
    fetchCameraRawData(base, editId, ac.signal)
      .then((data) => {
        setValues(mapApiRecordToAddCameraForm(data));
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name === "AbortError") return;
        setLoadError(
          e instanceof Error ? e.message : "Failed to load camera",
        );
        setValues({ ...DEFAULT_VALUES });
      })
      .finally(() => setLoadingEdit(false));

    return () => ac.abort();
  }, [open, editingCameraId, apiBase]);

  const handleApply = async () => {
    if (loadingEdit || loadError) return;
    setSubmitting(true);
    try {
      const editId = editingCameraId?.trim();
      if (editId && onEditApply) {
        await onEditApply(editId, values);
      } else {
        await onApply(values);
      }
      onClose();
    } catch (e) {
      /**
       * {@link ZoneManagementTab} throws these after toast + user-facing handling so the
       * dialog must stay open and the error must not hit the global React overlay.
       */
      if (
        e instanceof Error &&
        (e.message === "ADD_CAMERA_HANDLED" ||
          e.message === "EDIT_CAMERA_HANDLED" ||
          e.message === "No API base")
      ) {
        return;
      }
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const formDisabled = loadingEdit || Boolean(loadError);
  const canSave =
    Boolean(values.cameraName?.trim()) && Boolean(values.streamUrl?.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box
          sx={{
            display: "inline-block",
            bgcolor: "#D32F2F",
            color: "#fff",
            px: 1.5,
            py: 0.5,
            borderRadius: "4px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            Camera config
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        {loadingEdit ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={36} sx={{ color: "#2932E5" }} />
          </Box>
        ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {loadError ? (
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                color: "#D32F2F",
              }}
            >
              {loadError}
            </Typography>
          ) : null}
          <Box>
            <RequiredLabelWithInfo
              infoTitle="Display name for this camera in lists and zone management."
              infoAriaLabel="About camera name"
            >
              Camera name
            </RequiredLabelWithInfo>
            <TextField
              fullWidth
              size="small"
              value={values.cameraName}
              disabled={formDisabled}
              onChange={(e) =>
                setValues((p) => ({ ...p, cameraName: e.target.value }))
              }
              placeholder="e.g. North entrance"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />
          </Box>

          <Box>
            <RequiredLabelWithInfo
              infoTitle="Full streaming URL sent to the API as streaming_url."
              infoAriaLabel="About stream URL"
            >
              stream_url
            </RequiredLabelWithInfo>
            <TextField
              fullWidth
              size="small"
              value={values.streamUrl}
              disabled={formDisabled}
              onChange={(e) =>
                setValues((p) => ({ ...p, streamUrl: e.target.value }))
              }
              placeholder="rtsp://host:554/path"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
            />
          </Box>
        </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          justifyContent: "flex-end",
          gap: 1,
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Button
          onClick={onClose}
          disabled={submitting || loadingEdit}
          sx={{
            textTransform: "uppercase",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            color: "#2932E5",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleApply();
          }}
          disabled={
            submitting || loadingEdit || Boolean(loadError) || !canSave
          }
          sx={{
            textTransform: "uppercase",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            bgcolor: "#E85D2C",
            boxShadow: "none",
            px: 2.5,
            "&:hover": {
              bgcolor: "#c94a22",
              boxShadow: "none",
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
